import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Input, Space, Switch, Tag, Typography } from 'antd';
import { AI_SKILL_LIMITS, AiChatSkill } from '../../../api/aiAssistant';
import AiChatSkillExamplesInput from './AiChatSkillExamplesInput';

interface Props {
  /** 受控值，由外层 Form.Item name="skills" 注入 */
  value?: AiChatSkill[];
  onChange?: (value: AiChatSkill[]) => void;
}

/**
 * 技能编辑器：管理员自由增删技能，逐条配置开关、名称、适用场景、提示词与示例提问。
 * 开启的技能会拼进 system prompt，其示例提问用于 AI 助手空会话的引导按钮。
 */
export default function AiChatSkillsField({ value, onChange }: Props) {
  const skills = value || [];
  const enabledCount = skills.filter((skill) => skill.enabled).length;

  const patch = (index: number, changes: Partial<AiChatSkill>) => {
    onChange?.(skills.map((skill, i) => (i === index ? { ...skill, ...changes } : skill)));
  };

  const add = () => {
    onChange?.([...skills, { name: '', description: '', prompt: '', examples: [], enabled: true }]);
  };

  const remove = (index: number) => {
    onChange?.(skills.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={add}
          disabled={skills.length >= AI_SKILL_LIMITS.maxSkills}
        >
          新增技能
        </Button>
        <Typography.Text type="secondary">
          共 {skills.length} 个，已开启 {enabledCount} 个（最多 {AI_SKILL_LIMITS.maxSkills} 个）
        </Typography.Text>
      </Space>
      {skills.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无技能，新增后开启即可拼进 AI 助手的 System Prompt"
        />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {skills.map((skill, index) => (
            // 技能没有稳定 id（整份配置覆盖保存），只能用下标做 key；
            // 删除靠 onChange 整体替换，不存在同 key 复用错行的问题
            <Card
              key={index}
              size="small"
              data-testid={`ai-skill-card-${index}`}
              title={
                <Space>
                  <Switch
                    size="small"
                    checked={!!skill.enabled}
                    onChange={(checked) => patch(index, { enabled: checked })}
                    aria-label={`技能开关 ${index + 1}`}
                  />
                  <Tag color={skill.enabled ? 'green' : 'default'}>
                    {skill.enabled ? '已开启' : '已关闭'}
                  </Tag>
                  <Input
                    placeholder="技能名称，如：放款复盘"
                    value={skill.name}
                    maxLength={AI_SKILL_LIMITS.maxNameChars}
                    style={{ width: 220 }}
                    onChange={(e) => patch(index, { name: e.target.value })}
                    aria-label={`技能名称 ${index + 1}`}
                  />
                </Space>
              }
              extra={
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => remove(index)}
                  aria-label={`删除技能 ${index + 1}`}
                />
              }
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Input
                  placeholder="适用场景（可选），如：问到放款金额、笔数时套用"
                  value={skill.description}
                  maxLength={AI_SKILL_LIMITS.maxDescriptionChars}
                  onChange={(e) => patch(index, { description: e.target.value })}
                  aria-label={`技能说明 ${index + 1}`}
                />
                <Input.TextArea
                  rows={3}
                  placeholder="技能提示词：这个场景下要模型怎么做（会拼进 System Prompt）"
                  value={skill.prompt}
                  maxLength={AI_SKILL_LIMITS.maxPromptChars}
                  showCount
                  onChange={(e) => patch(index, { prompt: e.target.value })}
                  aria-label={`技能提示词 ${index + 1}`}
                />
                <AiChatSkillExamplesInput
                  placeholder={`示例提问，每行一条，最多 ${AI_SKILL_LIMITS.maxExamples} 条（展示在空会话引导区）`}
                  value={skill.examples}
                  onChange={(examples) => patch(index, { examples })}
                  ariaLabel={`技能示例提问 ${index + 1}`}
                />
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}
